/**
 * @module Hooks.UsePlanPricing
 */
import { useTranslation } from "react-i18next";
import { PromoDiscount } from "app/models/PromoOffer";
import { getDiscountedAmount } from "app/utils/planPricing";

interface UsePlanPricingParams {
  // The plan's live base price (cents), from the backend resolution.
  baseAmount?: number;
  // The resolved offer's discount, or null/undefined.
  discount?: PromoDiscount | null;
  isYearly: boolean;
}

export interface PlanPricing {
  // Base price with the initial-resolve fallback applied (0 while loading).
  base: number;
  // First-period price after the discount (equals `base` without one).
  discountedAmount: number;
  isDiscounted: boolean;
  // Billing period word / clause, already localized ("year" / "billed annually").
  interval: string;
  billing: string;
  // A "forever" coupon lowers every period ("per year"); any temporary coupon
  // (once / repeating) only discounts the opening one ("per first year").
  discountedI18nKey: string;
}

/**
 * Derived pricing state shared by the checkout PriceCards, so the plan cards
 * don't each re-implement the discount math and the discounted-line wording
 * choices. Markup stays with the cards — this hook only computes.
 */
export const usePlanPricing = ({
  baseAmount,
  discount,
  isYearly,
}: UsePlanPricingParams): PlanPricing => {
  const { t } = useTranslation();
  // 0 only during the initial resolve, while the cards' `loading` gates the
  // price rendering.
  const base = baseAmount ?? 0;
  const discountedAmount = getDiscountedAmount(base, discount);

  return {
    base,
    discountedAmount,
    isDiscounted: discountedAmount < base,
    interval: isYearly ? t("year") : t("month"),
    billing: isYearly ? t("billed annually") : t("billed monthly"),
    discountedI18nKey:
      discount?.duration === "forever"
        ? "trial_price_discounted"
        : "trial_price_discounted_first",
  };
};
